import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import logo from '../../assets/images/The-Key-Logo.png';
import apiClient from "../../utils/api";

function ParkingCustomerTicket() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parking, setParking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ticketRef = useRef(null);

  // Fetch parking data using the ID from URL params
  useEffect(() => {
    const fetchParkingData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        const response = await apiClient.get(`accounts/parkings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });        
        console.log('API response:', response.data.data);
        setParking(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching parking data:', err);
        setError('Failed to fetch parking data');
        setLoading(false);
      }
    };    
    fetchParkingData();
  }, [id]);

  useEffect(() => {
    if (!parking) return;
    
    console.log('Printing ticket:', parking);
    // Add a small delay to ensure the component is fully rendered
    setTimeout(() => {
      printTicket();
    }, 500);
  }, [parking]); // Re-run when parking data changes

  const printTicket = async () => {
    // Extract ticket data
    const {
      ticket_number: serial = '00001',
      plate_number: plate = '',
      model = null,
      color = null,
      brand = null,
      parking_time = '',
      parking_date = '',
      parking_type = '',
      qr_code_url = '',
      qr_code_base64 = '',
      exit_location_logo_url = '',
      exit_location_logo_base64 = '',
      exit_location = null,
    } = parking || {};
    
    const modelName = model?.name || '-';
    const colorName = color?.name || '-';
    const brandName = brand?.name || '-';
    const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || '-');
    const plateDisplay = plate || '-';
    const parkingDateTime = ( parking_time && parking_date )? 
      `${parking_date} ${parking_time}` : '-';
    const serialDisplay = serial || '-';

    // Prepare QR code data for printing
    let qrCodeBase64 = '';
    let qrCodeForPrint = '';

    // Prepare exit location logo data for printing
    let logoBase64 = '';
    let logoForPrint = '';

    // Process exit location logo
    if (exit_location_logo_base64 || (exit_location && exit_location.logo_base64)) {
      const base64Data = exit_location_logo_base64 || exit_location.logo_base64;
      logoBase64 = `data:image/png;base64,${base64Data}`;
      // For thermal printing, resize to 30mm width
      logoForPrint = await resizeImageForThermalPrint(logoBase64, 30);
    }
    else if (exit_location_logo_url || (exit_location && exit_location.logo_url)) {
      try {
        const logoUrl = exit_location_logo_url || exit_location.logo_url;
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        const result = await new Promise((resolve) => {
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(blob);
        });

        // Store the full base64 data URL for display
        logoBase64 = result;

        // For thermal printing, resize to 30mm width
        logoForPrint = await resizeImageForThermalPrint(result, 30);

      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    }

    // Process QR code
    if (qr_code_base64) {
      // We already have the base64 data from the API
      qrCodeBase64 = `data:image/png;base64,${qr_code_base64}`;
      // For thermal printing, resize to 30mm width
      qrCodeForPrint = await resizeImageForThermalPrint(qrCodeBase64, 30);
    }
    else if (qr_code_url) {
      try {
        const response = await fetch(qr_code_url);
        const blob = await response.blob();
        const reader = new FileReader();
        const result = await new Promise((resolve) => {
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(blob);
        });

        // Store the full base64 data URL for display
        qrCodeBase64 = result;

        // For thermal printing, resize to 30mm width
        qrCodeForPrint = await resizeImageForThermalPrint(result, 30);

      } catch (error) {
        console.error('Error fetching QR code:', error);
      }
    }
    
    // Create ticket content for rawbt printing
    const ticketContent = `
${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'.'.repeat(32)}
     Valet Parking Ticket
${'-'.repeat(32)}
Plate No.     : ${plateDisplay}
Brand         : ${brandName}
Car Model     : ${modelName}
Color         : ${colorName}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
  Your car is in safe hands
${'.'.repeat(32)}
Please order your car 15mins in advance. Dial ${parking.exit_location?.phone_number || '+974 5538 0499'} for  assistance.
${'-'.repeat(32)}
`;

// Name             Name
// Time             Time
// ${'-'.repeat(32)}

    // TODO: Implement Arabic text printing in the future
    // Arabic text to be added later:
    // بطاقة صف السيارة
    // رقم اللوحة         نوع السيارة
    // اللون              رقم الوقف
    // الاسم             الاسم
    // الوقت             الوقت
    // سيارتك في أيدٍ أمينة
    // يرجى طلب سيارتك قبل 15 دقيقة

    // Detect user agent for appropriate printing method
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.indexOf("android") > -1;
    
    if (isAndroid) {
      // Use Intent scheme for Android devices with enhanced printing
      await androidPrintTicket(ticketContent, qrCodeForPrint, logoForPrint);
    } else {
      // Use WebSocket for desktop/PC
      desktopPrint(ticketContent, qrCodeForPrint);
    }
  };

  // Helper function to resize images for thermal printing
  const resizeImageForThermalPrint = (dataUrl, targetWidthMm) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Convert mm to pixels (assuming 203 DPI for thermal printer)
        const dpi = 203;
        const mmToPx = dpi / 25.4;
        const targetWidthPx = Math.round(targetWidthMm * mmToPx);
        
        // Calculate height to maintain aspect ratio
        const aspectRatio = img.height / img.width;
        const targetHeightPx = Math.round(targetWidthPx * aspectRatio);
        
        // Set canvas dimensions
        canvas.width = targetWidthPx;
        canvas.height = targetHeightPx;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, targetWidthPx, targetHeightPx);
        
        // Convert back to data URL
        const resizedDataUrl = canvas.toDataURL('image/png');
        resolve(resizedDataUrl);
      };
      
      img.src = dataUrl;
    });
  };

  // Helper function to create a combined image with logo and QR code side by side
  const createCombinedLogoAndQR = (logoData, qrData) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size for thermal printer (72mm width)
      const dpi = 203;
      const mmToPx = dpi / 25.4;
      const canvasWidth = Math.round(72 * mmToPx); // 72mm width
      const imageWidth = Math.round(30 * mmToPx);  // 30mm for each image
      const spacing = Math.round(12 * mmToPx);     // 12mm spacing between images
      
      let imagesLoaded = 0;
      const logoImg = new Image();
      const qrImg = new Image();
      
      const checkIfBothLoaded = () => {
        imagesLoaded++;
        if (imagesLoaded === 2) {
          // Calculate heights maintaining aspect ratio
          const logoAspectRatio = logoImg.height / logoImg.width;
          const qrAspectRatio = qrImg.height / qrImg.width;
          
          const logoHeight = Math.round(imageWidth * logoAspectRatio);
          const qrHeight = Math.round(imageWidth * qrAspectRatio);
          
          // Canvas height should accommodate the taller image
          const canvasHeight = Math.max(logoHeight, qrHeight);
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          
          // Fill with white background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvasWidth, canvasHeight);
          
          // Draw logo on the left
          const logoX = Math.round((canvasWidth / 2 - spacing / 2) / 2 - imageWidth / 2);
          const logoY = Math.round((canvasHeight - logoHeight) / 2);
          ctx.drawImage(logoImg, logoX, logoY, imageWidth, logoHeight);
          
          // Draw QR code on the right
          const qrX = Math.round(canvasWidth / 2 + spacing / 2 + (canvasWidth / 2 - spacing / 2) / 2 - imageWidth / 2);
          const qrY = Math.round((canvasHeight - qrHeight) / 2);
          ctx.drawImage(qrImg, qrX, qrY, imageWidth, qrHeight);
          
          // Convert to data URL
          const combinedDataUrl = canvas.toDataURL('image/png');
          resolve(combinedDataUrl);
        }
      };
      
      logoImg.onload = checkIfBothLoaded;
      qrImg.onload = checkIfBothLoaded;
      
      logoImg.src = logoData;
      qrImg.src = qrData;
    });
  };

  const androidPrintTicket = async (textData, qrCodeData = '', logoData = '') => {
    try {
      const beforeUrl = 'intent:';
      const afterUrl = '#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;';
      
      // Create the print queue with appropriate delays
      const printQueue = [];

      // Step 1: Add logo and QR code first
      if (logoData && qrCodeData) {
        try {
          const combinedImage = await createCombinedLogoAndQR(logoData, qrCodeData);
          printQueue.push(() => {
            console.log('Step 1: Printing combined logo and QR code');
            try {
              const combinedIntentUrl = `intent:${combinedImage}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
              window.location.href = combinedIntentUrl;
            } catch (combinedError) {
              console.error('Error printing combined image:', combinedError);
              try {
                const directCombinedUrl = `rawbt:${combinedImage}`;
                window.location.href = directCombinedUrl;
                console.log('Fallback: Using direct rawbt:data scheme for combined image');
              } catch (directError) {
                console.error('Combined image printing failed:', directError);
              }
            }
            return 800; // Return delay before next item (reduced delay)
          });
        } catch (error) {
          console.error('Error creating combined image, falling back to separate images:', error);
          // Fall back to separate images
          if (logoData) {
            printQueue.push(() => {
              console.log('Step 1a: Printing exit location logo');
              try {
                const logoIntentUrl = `intent:${logoData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
                window.location.href = logoIntentUrl;
              } catch (logoError) {
                console.error('Error printing exit location logo:', logoError);
                try {
                  const directLogoUrl = `rawbt:${logoData}`;
                  window.location.href = directLogoUrl;
                  console.log('Fallback: Using direct rawbt:data scheme for logo');
                } catch (directError) {
                  console.error('Logo printing failed:', directError);
                }
              }
              return 600;
            });
          }

          if (qrCodeData) {
            printQueue.push(() => {
              console.log('Step 1b: Printing QR code');
              try {
                const qrIntentUrl = `intent:${qrCodeData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
                window.location.href = qrIntentUrl;
              } catch (qrError) {
                console.error('Error printing QR code:', qrError);
                try {
                  const directQrUrl = `rawbt:${qrCodeData}`;
                  window.location.href = directQrUrl;
                  console.log('Fallback: Using direct rawbt:data scheme for QR code');
                } catch (directError) {
                  console.error('QR code printing failed:', directError);
                }
              }
              return 800; 
            });
          }
        }
      } else {
        // Handle individual images if only one is available
        if (logoData) {
          printQueue.push(() => {
            console.log('Step 1a: Printing exit location logo');
            try {
              const logoIntentUrl = `intent:${logoData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
              window.location.href = logoIntentUrl;
            } catch (logoError) {
              console.error('Error printing exit location logo:', logoError);
              try {
                const directLogoUrl = `rawbt:${logoData}`;
                window.location.href = directLogoUrl;
                console.log('Fallback: Using direct rawbt:data scheme for logo');
              } catch (directError) {
                console.error('Logo printing failed:', directError);
              }
            }
            return 600; 
          });
        }

        if (qrCodeData) {
          printQueue.push(() => {
            console.log('Step 1b: Printing QR code');
            try {
              const qrIntentUrl = `intent:${qrCodeData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
              window.location.href = qrIntentUrl;
            } catch (qrError) {
              console.error('Error printing QR code:', qrError);
              try {
                const directQrUrl = `rawbt:${qrCodeData}`;
                window.location.href = directQrUrl;
                console.log('Fallback: Using direct rawbt:data scheme for QR code');
              } catch (directError) {
                console.error('QR code printing failed:', directError);
              }
            }
            return 800; 
          });
        }
      }
      
      // Step 2: Add customer ticket to queue
      printQueue.push(() => {
        console.log('Step 2: Printing customer ticket...');
        const textEncoded = encodeURIComponent(textData);
        const textIntentUrl = beforeUrl + textEncoded + afterUrl;
        window.location.href = textIntentUrl;
        return 300; // Reduced delay before completion
      });
      
      // Execute the print queue sequentially with delays
      const executePrintQueue = (index) => {
        if (index >= printQueue.length) {
          console.log('All print jobs completed');
          return;
        }
        
        const printJob = printQueue[index];
        const delay = printJob();
        
        if (delay && delay > 0) {
          setTimeout(() => {
            executePrintQueue(index + 1);
          }, delay);
        } else {
          // If no delay specified, move to next immediately
          executePrintQueue(index + 1);
        }
      };
      
      // Debug: Show the queue length before execution
      console.log(`Print queue initialized with ${printQueue.length} items`);
      
      // Start executing the print queue with a small initial delay
      setTimeout(() => {
        executePrintQueue(0);
      }, 100);
      
      console.log('Enhanced Android printing initiated');
    } catch (error) {
      console.error('Error in enhanced Android printing:', error);
      // Fallback to simple printing
      fallbackSimpleAndroidPrint(textData, qrCodeData);
    }
  };

  const fallbackSimpleAndroidPrint = (textData, qrCodeData = '') => {
    try {
      // First, print the text content
      const beforeUrl = 'intent:';
      const afterUrl = '#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;';
      
      const textEncoded = encodeURIComponent(textData);
      const textIntentUrl = beforeUrl + textEncoded + afterUrl;
      
      console.log('Sending text to Android RawBT:', textIntentUrl);
      window.location.href = textIntentUrl;
      
      // If there's a QR code, print it separately after a short delay
      if (qrCodeData) {
        setTimeout(() => {
          try {
            // Use the rawbt:data scheme for image printing as per documentation
            const qrIntentUrl = `intent:${qrCodeData}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
            console.log('Sending QR code to Android RawBT:', qrIntentUrl);
            window.location.href = qrIntentUrl;
          } catch (qrError) {
            console.error('Error sending QR code to Android RawBT app:', qrError);
            // Alternative: try direct rawbt:data scheme
            try {
              const directQrUrl = `rawbt:${qrCodeData}`;
              window.location.href = directQrUrl;
              console.log('Fallback: Using direct rawbt:data scheme for QR code');
            } catch (directError) {
              console.error('QR code printing failed:', directError);
            }
          }
        }, 1000); // 1 second delay to allow the first print to complete
      }
      
      console.log('Ticket sent to Android RawBT app via intent scheme');
    } catch (error) {
      console.error('Error sending to Android RawBT app:', error);
      // Fallback to alternative method
      // try {
      //   const rawbtUrl = `rawbt:${encodeURIComponent(textData)}`;
      //   window.location.href = rawbtUrl;
      //   console.log('Fallback: Using rawbt scheme');
      // } catch (fallbackError) {
      //   console.error('All Android methods failed:', fallbackError);
      // }
    }
  };

  const desktopPrint = (textData, qrCodeData = '') => {
    // Using WebSocket connection for desktop with improved error handling
    try {
      const socket = new WebSocket("ws://127.0.0.1:40213/");
      
      socket.onerror = function(error) {
        console.error("WebSocket Error:", error);
        // Fallback to browser print
        fallbackBrowserPrint();
      };
      
      socket.onopen = function() {
        console.log('WebSocket connected, sending ticket data');
        
        // Send the text data first
        socket.send(textData);
        
        // If there's QR code data, send it separately
        if (qrCodeData) {
          // For desktop RawBT server, send the QR code as a separate WebSocket message
          setTimeout(() => {
            try {
              // Create a new WebSocket connection for the QR code
              const qrSocket = new WebSocket("ws://127.0.0.1:40213/");
              qrSocket.onopen = function() {
                // Send the QR code data with proper rawbt formatting
                qrSocket.send(`rawbt:${qrCodeData}`);
                qrSocket.close(1000, "QR code sent");
                console.log('QR code sent to desktop RawBT server');
              };
              qrSocket.onerror = function(error) {
                console.error('QR code WebSocket error:', error);
              };
            } catch (qrError) {
              console.error('Error sending QR code:', qrError);
            }
          }, 500); // Small delay between text and QR code
        }
        
        // Close the main socket after sending text
        setTimeout(() => {
          socket.close(1000, "Text sent");
        }, 200);
        
        console.log('Ticket sent to desktop RawBT server');
      };
      
      socket.onclose = function(event) {
        if (event.code !== 1000) {
          console.warn('WebSocket closed unexpectedly, trying browser print');
          fallbackBrowserPrint();
        }
      };
      
      // Timeout fallback
      setTimeout(() => {
        if (socket.readyState === WebSocket.CONNECTING) {
          socket.close();
          fallbackBrowserPrint();
        }
      }, 5000);
      
    } catch (error) {
      console.error('Error connecting to desktop RawBT server:', error);
      fallbackBrowserPrint();
    }
  };

  const fallbackBrowserPrint = () => {
    console.log('Using browser print as fallback');
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // If loading, show loading indicator
  if (loading) {
    return <div>Loading ticket data...</div>;
  }

  // If error, show error message
  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <button 
          onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            fontSize: '16px'
          }}
        >
          ← Back to Print Options
        </button>
        <div>Error: {error}</div>
      </div>
    );
  }

  // If no parking data, show message
  if (!parking) {
    return (
      <div style={{ padding: '20px' }}>
        <button 
          onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            fontSize: '16px'
          }}
        >
          ← Back to Print Options
        </button>
        <div>No parking data available</div>
      </div>
    );
  }

  // Extract ticket data from the parking object
  const {
    ticket_number: serial = '00001',
    plate_number: plate = '',
    model = null,
    color = null,
    brand = null,
    parking_time = '',
    parking_date = '',
    parking_type = ''
  } = parking || {};
  
  // Extract values from relationship objects
  const modelName = model?.name || '-';
  const colorName = color?.name || '-';
  const brandName = brand?.name || '-';   
  // Determine what to display for the parking type label
  const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || '-');
  const plateDisplay = plate || '-';
  const parkingDateTime = parking_time && parking_date ? 
    `${parking_date} ${parking_time}` : '-';
  const serialDisplay = serial || '-';
  return (
    <div>
      <button 
        onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
        style={{
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer',
          margin: '20px',
          fontSize: '16px',
          position: 'fixed',
          top: '10px',
          left: '10px',
          zIndex: 1000
        }}
      >
        ← Back to Print Options
      </button>
      <div className="print-ticket-container" ref={ticketRef}>
      <style>
        {`
        @page {
          margin: 0;
          size: 80mm auto;
        }
        .print-ticket-container {
          width: 72mm;
          padding: 1mm;
          font-family: Arial, sans-serif;
          color: #333;
          text-align: center;
        }
        .ticket-header {
          text-align: center;
          margin-bottom: 1mm;
        }
        .ticket-logo {
          width: 25mm;
          margin-bottom: 1mm;
        }        
        .ticket-serial {
          margin: 1mm 0;
          display: flex;
          align-items: center;
          justify-content: left;
          gap: 2mm;
        }
        .ticket-serial-label {
          font-size: 3mm;
        }
        .serial-number {
          color: #FF0000;
          font-size: 6mm;
          font-weight: bold;
        }
        .free-label {
          color: #967c3d;
          font-size: 4mm;
          margin: 1mm 0;
          text-align: left;
        }
        .dotted-divider {
          border: none;
          border-top: 0.5mm dotted #FF0000;
          margin: 1mm 0;
        }
        .ticket-title {
          background: #967c3d;
          color: white;
          padding: 1mm;
          text-align: center;
          border-radius: 1mm;
          margin-bottom: 1mm;
        }
        .ticket-details {
          width: 100%;
          margin-bottom: 1mm;
          border-collapse: collapse;
        }
        .ticket-details td {
          padding: 1mm;
          font-size: 2.5mm;
          text-align: left;
        }
        .arabic-text {
          color: #967c3d;
          font-size: 2.5mm;
        }
        .names-grid {
          width: 100%;
          background: #967c3d;
          color: white;
          border-radius: 1mm;
          margin: 1mm 0;
        }
        .names-grid th, .names-grid td {
          padding: 1mm;
          text-align: center;
          font-size: 2.5mm;
        }
        .logo-large {
          text-align: center;
          margin: 1mm 0;
        }
        .logo-large img {
          width: 35mm;
        }
        .safe-hands {
          text-align: center;
          border: 0.2mm solid #967c3d;
          border-radius: 3mm;
          padding: 1mm;
          margin: 1mm 0;
        }
        .footer {
          text-align: center;
        }
        .order-text {
          border: 0.2mm solid #967c3d;
          border-radius: 3mm;
          padding: 1mm;
          margin: 1mm 0;
        }
        .phone-number {
          font-size: 3mm;
          margin: 1mm 0;
        }
        .qr-code-section {
          margin: 2mm 0;
        }
        .qr-code-container {
          text-align: center;
        }
        .qr-code-image {
          width: 20mm;
          height: 20mm;
          margin: 1mm auto;
          display: block;
        }
        .exit-location-logo-section {
          margin: 2mm 0;
        }
        .exit-location-logo-container {
          text-align: center;
        }
        .exit-location-logo-image {
          max-width: 40mm;
          max-height: 20mm;
          margin: 1mm auto;
          display: block;
        }
        @media print {
          body { margin: 0; padding: 0; }
          .print-ticket-container { 
            width: 72mm;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .ticket-title, .names-grid {
            background: #967c3d !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .serial-number {
            color: #FF0000 !important;
          }
          .free-label, .arabic-text {
            color: #967c3d !important;
          }
          .qr-code-image {
            width: 20mm !important;
            height: 20mm !important;
          }
          .exit-location-logo-image {
            max-width: 40mm !important;
            max-height: 20mm !important;
          }
        }
        `}
      </style>      
      <div className="ticket-header">
        <img src={logo} alt="Key Valet" className="ticket-logo" />
        <div className="ticket-serial">
          <span className="ticket-serial-label">Ticket No:</span>
          <span className="serial-number">{serialDisplay}</span>
        </div>        <div className="free-label" style={{ paddingLeft: '1mm' }}>{parkingTypeDisplay}</div>
      </div>

      <hr className="dotted-divider" />

      <div className="ticket-title">
        <div className="arabic-text">بطاقة صف السيارة</div>
        <div>Valet Parking Ticket</div>
      </div>

      <table className="ticket-details">
        <tbody>          <tr>
            <td>Plate No.:<br/><span className="arabic-text">رقم اللوحة:</span></td>
            <td>{plateDisplay}</td>
            <td>Car Model:<br/><span className="arabic-text">نوع السيارة:</span></td>
            <td>{modelName}</td>
          </tr>
          <tr>
            <td>Color:<br/><span className="arabic-text">اللون:</span></td>
            <td>{colorName}</td>
            <td>Brand:<br/><span className="arabic-text">الماركة:</span></td>
            <td>{brandName}</td>
          </tr>
          <tr>
            <td>Parking Time:<br/><span className="arabic-text">وقت الوقوف:</span></td>
            <td>{parkingDateTime}</td>
          </tr>
        </tbody>
      </table>

      {/* <table className="names-grid">
        <thead>
          <tr>
            <th>Name<br/><span className="arabic-text">الاسم</span></th>
            <th>Name<br/><span className="arabic-text">الاسم</span></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Time<br/><span className="arabic-text">الوقت</span></td>
            <td>Time<br/><span className="arabic-text">الوقت</span></td>
          </tr>
        </tbody>
      </table>       */}
      
      {/* <div className="free-label">{parkingTypeDisplay}</div>
      <div className="serial-number">{serialDisplay}</div> */}

      <hr className="dotted-divider" />

      <div className="logo-large">
        <img src={logo} alt="Key Valet Large" />
      </div>

      <div className="safe-hands">
        <div className="arabic-text">سيارتك في أيدٍ أمينة</div>
        <div>Your car is in safe hands</div>
      </div>

      <hr className="dotted-divider" />

      <div className="footer">
        <div className="order-text">
          <div className="arabic-text">يرجى طلب سيارتك قبل 15 دقيقة</div>
          <div>Please order your car 15mins in advance. </div>
        </div>
        <div className="phone-number">
          <i className="fa fa-phone"></i> Dial {parking.exit_location?.phone_number || '+974 5538 0499'} for assistance.
        </div>
        
        {/* Exit Location Logo Display */}
        {(parking.exit_location_logo_base64 || parking.exit_location_logo_url || 
          (parking.exit_location && (parking.exit_location.logo_base64 || parking.exit_location.logo_url))) && (
          <div className="exit-location-logo-section">
            <hr className="dotted-divider" />
            <div className="exit-location-logo-container">
              <img 
                src={parking.exit_location_logo_base64 
                  ? `data:image/png;base64,${parking.exit_location_logo_base64}`
                  : parking.exit_location_logo_url || 
                    (parking.exit_location?.logo_base64 
                      ? `data:image/png;base64,${parking.exit_location.logo_base64}`
                      : parking.exit_location?.logo_url)
                } 
                alt="Exit Location Logo" 
                className="exit-location-logo-image" 
              />
              <div style={{ fontSize: '2mm', marginTop: '1mm' }}>Exit Location</div>
            </div>
          </div>
        )}
        
        {/* QR Code Display */}
        {(parking.qr_code_base64 || parking.qr_code_url) && (
          <div className="qr-code-section">
            <hr className="dotted-divider" />
            <div className="qr-code-container">
              <img 
                src={parking.qr_code_base64 
                  ? `data:image/png;base64,${parking.qr_code_base64}` 
                  : parking.qr_code_url
                } 
                alt="QR Code" 
                className="qr-code-image" 
              />
              <div style={{ fontSize: '2mm', marginTop: '1mm' }}>Scan for tracking</div>
            </div>
          </div>
        )}
        
        {/* <div className="free-label">{parkingTypeDisplay}</div>
        <div className="serial-number">{serialDisplay}</div> */}
      </div>
    </div>
    </div>
  );
}

export default ParkingCustomerTicket;